/* eslint-disable react-hooks/exhaustive-deps */
import ContentShell from "components/ContentShell";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import TuneIcon from "@mui/icons-material/Tune";
import LanguageIcon from "@mui/icons-material/Language";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import SimpleSwitch from "components/Switch";
import { SimpleSelectInput } from "components/FormSelectInput";
import sessionHelper from "services/sessionHelper";
import {
  getTutorialConfig,
  uploadTutorialVideo,
  deleteTutorialVideo,
} from "services/tutorialService";
import VideoPlayer from "./components/VideoPlayer";
import VideoUploader from "./components/VideoUploader";

interface TutorialVideo {
  languageCode: string;
  videoPath: string;
  videoUrl?: string;
  metadata?: {
    fileSize?: number;
    container?: string;
    mimeType?: string;
  };
  uploadedBy?: string;
  uploadedAt?: string;
}

interface TutorialState {
  operatorId: string;
  operatorName: string;
  videos: TutorialVideo[];
}

const initialState: TutorialState = {
  operatorId: "",
  operatorName: "",
  videos: [],
};

// ─── Section Card (matches ChallengesEditor pattern) ─────────
const SectionCard = ({
  step,
  icon,
  title,
  subtitle,
  accent = "#1976d2",
  children,
  isComplete,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: string;
  children: React.ReactNode;
  isComplete?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: isComplete ? `${accent}60` : "divider",
      borderRadius: 3,
      mb: 3,
      overflow: "hidden",
      transition: "border-color 0.3s, box-shadow 0.3s",
      "&:hover": { boxShadow: `0 2px 16px ${accent}12` },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(135deg, ${accent}0a 0%, transparent 100%)`,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}40`,
        }}
      >
        {step}
      </Box>
      <Box sx={{ color: accent, display: "flex", fontSize: 20 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          color="text.primary"
          sx={{ lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      {isComplete !== undefined && (
        <Tooltip
          title={isComplete ? "Section complete" : "Fill in required fields"}
        >
          <Box
            sx={{
              display: "flex",
              color: isComplete ? "#4caf50" : "#bdbdbd",
            }}
          >
            {isComplete ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" />
            )}
          </Box>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Paper>
);

// ─── Format Helpers ──────────────────────────────────────────
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

// ─── Component ───────────────────────────────────────────────
const TutorialEditor = () => {
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const operatorId = location.pathname.split("/").slice(-1)[0];
  const [loading, setLoading] = useState(false);
  const [tutorialConfig, setTutorialConfig] =
    useState<TutorialState>(initialState);

  // Add-new-video state
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");

  const cancel = () => navigate(configData.routes.tutorials.tutorials);

  const getOperatorName = (opId: string): string => {
    const op = (sessionHelper.allowedOperators || []).find(
      (o: any) => o._id === opId,
    );
    return op?.operatorName || opId;
  };

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const resp = await getTutorialConfig(operatorId);
      const data = resp?.data;
      const cacheBuster = `t=${Date.now()}`;
      const videos = (data?.videos || []).map((v: TutorialVideo) => ({
        ...v,
        videoUrl: v.videoUrl
          ? `${v.videoUrl.split("?")[0]}?${cacheBuster}`
          : v.videoUrl,
      }));
      setTutorialConfig({
        operatorId,
        operatorName: getOperatorName(operatorId),
        videos,
      });
    } catch {
      setTutorialConfig({
        operatorId,
        operatorName: getOperatorName(operatorId),
        videos: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (languageCode: string, file: File) => {
    await uploadTutorialVideo(operatorId, languageCode, file);
    showSnackbar("Video uploaded successfully", "success");
    setShowAddForm(false);
    setSelectedLanguage("");
    await fetchConfig();
  };

  const handleDelete = async (languageCode: string) => {
    const resp = await deleteTutorialVideo(operatorId, languageCode);
    showSnackbar(resp?.message || "Video deleted successfully", "success");
    await fetchConfig();
  };

  useEffect(() => {
    if (operatorId) fetchConfig();
  }, [operatorId]);

  // Languages that already have a video uploaded
  const uploadedLanguageCodes = new Set(
    tutorialConfig.videos.map((v) => v.languageCode),
  );

  // Available languages (all minus already uploaded)
  const allLanguages: { id: string; label: string }[] =
    sessionHelper.languageOptions || [];

  const availableLanguages = allLanguages.filter(
    (lang) => !uploadedLanguageCodes.has(lang.id),
  );

  const getLangLabel = (code: string): string => {
    const lang = allLanguages.find((l) => l.id === code);
    return lang ? `${lang.label} (${code})` : code;
  };

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`Tutorial Configuration \u2014 ${tutorialConfig.operatorName}`}
      loading={loading}
      actions={
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#d32f2f",
            "&:hover": { backgroundColor: "#b71c1c" },
          }}
          onClick={cancel}
        >
          Cancel
        </Button>
      }
    >
      <Box sx={{ maxWidth: 900, mx: "auto", py: 1 }}>
        {/* ─── Section 1: Add New Video ───────────────────── */}
        <SectionCard
          step={1}
          icon={<AddCircleOutlineIcon />}
          title="Add New Video"
          subtitle="Upload a tutorial video for an additional language"
          accent="#00796b"
        >
          {!showAddForm ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setShowAddForm(true)}
                disabled={availableLanguages.length === 0}
                sx={{
                  borderColor: "#00796b",
                  color: "#00796b",
                  "&:hover": {
                    borderColor: "#004d40",
                    backgroundColor: "#e0f2f1",
                  },
                }}
              >
                {availableLanguages.length === 0
                  ? "All languages have videos"
                  : "Add Video for Another Language"}
              </Button>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Box sx={{ width: 240 }}>
                  <SimpleSelectInput
                    name="language"
                    label="Select Language"
                    options={availableLanguages}
                    value={selectedLanguage}
                    onChange={(e: any) =>
                      setSelectedLanguage(e?.target?.value || "")
                    }
                    customStyle={{ height: 36 }}
                  />
                </Box>
                <Button
                  variant="text"
                  size="small"
                  sx={{ mt: 3, color: "#999" }}
                  onClick={() => {
                    setShowAddForm(false);
                    setSelectedLanguage("");
                  }}
                >
                  Cancel
                </Button>
              </Box>

              {selectedLanguage && (
                <Box
                  sx={{
                    p: 2,
                    border: "1px dashed #b2dfdb",
                    borderRadius: 2,
                    backgroundColor: "#f1f8f6",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ mb: 1.5, fontWeight: 600, color: "#00796b" }}
                  >
                    Upload video for: {getLangLabel(selectedLanguage)}
                  </Typography>
                  <VideoUploader
                    onUpload={(file) => handleUpload(selectedLanguage, file)}
                    onDelete={async () => {}}
                    hasVideo={false}
                    showDelete={false}
                  />
                </Box>
              )}
            </Box>
          )}
        </SectionCard>

        {/* ─── Section 2: Uploaded Videos ──────────────────── */}
        <SectionCard
          step={2}
          icon={<VideoLibraryIcon />}
          title="Uploaded Videos"
          subtitle={
            tutorialConfig.videos.length > 0
              ? `${tutorialConfig.videos.length} video(s) uploaded`
              : "No videos uploaded yet"
          }
          accent="#7b1fa2"
          isComplete={tutorialConfig.videos.length > 0}
        >
          {tutorialConfig.videos.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <VideoLibraryIcon
                sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }}
              />
              <Typography variant="body2" color="textSecondary">
                No videos have been uploaded yet. Use the section below to add
                your first video.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {tutorialConfig.videos.map((video) => (
                <Paper
                  key={video.languageCode}
                  elevation={0}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    overflow: "hidden",
                    transition: "box-shadow 0.2s",
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                  }}
                >
                  {/* Language header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.5,
                      backgroundColor: "#fafafa",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LanguageIcon
                        fontSize="small"
                        sx={{ color: "#7b1fa2" }}
                      />
                      <Typography variant="subtitle2" fontWeight={700}>
                        {getLangLabel(video.languageCode)}
                      </Typography>
                    </Box>
                    <Tooltip title="Delete this video">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(video.languageCode)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Video content */}
                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        flexWrap: { xs: "wrap", md: "nowrap" },
                      }}
                    >
                      {/* Preview */}
                      <Box sx={{ flexShrink: 0, width: 320 }}>
                        <VideoPlayer
                          src={video.videoUrl || ""}
                          label={video.languageCode}
                        />
                      </Box>

                      {/* Details + Replace */}
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          minWidth: 200,
                        }}
                      >
                        <Box>
                          {video.metadata?.container && (
                            <DetailRow
                              label="Container"
                              value={video.metadata.container}
                            />
                          )}
                          {video.metadata?.mimeType && (
                            <DetailRow
                              label="MIME Type"
                              value={video.metadata.mimeType}
                            />
                          )}
                          {video.metadata?.fileSize != null && (
                            <DetailRow
                              label="File Size"
                              value={formatFileSize(video.metadata.fileSize)}
                            />
                          )}
                          {video.uploadedBy && (
                            <DetailRow
                              label="Uploaded By"
                              value={video.uploadedBy}
                            />
                          )}
                          {video.uploadedAt && (
                            <DetailRow
                              label="Uploaded At"
                              value={formatDate(video.uploadedAt)}
                            />
                          )}
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <VideoUploader
                          onUpload={(file) =>
                            handleUpload(video.languageCode, file)
                          }
                          onDelete={() => handleDelete(video.languageCode)}
                          hasVideo={true}
                          metadata={video.metadata}
                          showDelete={false}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </SectionCard>
      </Box>
    </ContentShell>
  );
};

// ─── Detail Row Helper ─────────────────────────────────────────
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
    <Typography
      variant="body2"
      sx={{ color: "#888", minWidth: 100, fontSize: "12px" }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, fontSize: "12px", color: "#333" }}
    >
      {value}
    </Typography>
  </Box>
);

export default TutorialEditor;
